import React, { useState, useEffect } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowDownToLine, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Smartphone,
  Landmark,
  Bitcoin,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AgentWithdrawalsPage = () => {
  const [loading, setLoading] = useState(true);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [processedWithdrawals, setProcessedWithdrawals] = useState([]);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('all');

  const currencies = ['all', 'ZAR', 'USD', 'USDC', 'USDT', 'BTC', 'ETH', 'MATIC'];

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      // Fetch pending withdrawals for this agent
      const pendingRes = await fetch(`${API_URL}/api/agents/me/withdrawals?status=pending`, {
        credentials: 'include'
      });
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingWithdrawals(data.withdrawals || []);
      }

      // Fetch processed withdrawals
      const processedRes = await fetch(`${API_URL}/api/agents/me/withdrawals?status=completed,rejected&limit=50`, {
        credentials: 'include'
      });
      if (processedRes.ok) {
        const data = await processedRes.json();
        setProcessedWithdrawals(data.withdrawals || []);
      }
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (withdrawalId) => {
    if (!window.confirm('Mark this withdrawal as processed? Confirm you have delivered the cash/completed the transfer.')) return;
    
    setProcessing(withdrawalId);
    try {
      const response = await fetch(`${API_URL}/api/agents/me/withdrawals/${withdrawalId}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'completed' })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Processing failed');
      }
      
      toast.success('Withdrawal marked as completed!');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (withdrawalId) => {
    const reason = window.prompt('Enter reason for rejection:');
    if (!reason) return;
    
    setProcessing(withdrawalId);
    try {
      const response = await fetch(`${API_URL}/api/agents/me/withdrawals/${withdrawalId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Rejection failed');
      }
      
      toast.success('Withdrawal rejected');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const getMethodIcon = (method) => {
    const icons = {
      mobile_money: Smartphone,
      bank: Landmark,
      crypto: Bitcoin,
      cash: ArrowDownToLine
    };
    return icons[method] || AlertCircle;
  };

  const getMethodLabel = (method) => {
    const labels = {
      mobile_money: 'Mobile Money',
      bank: 'Bank Transfer',
      crypto: 'Cryptocurrency',
      cash: 'Cash Pickup'
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30', icon: Clock },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30', icon: XCircle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const filteredWithdrawals = (withdrawals) => {
    if (selectedCurrency === 'all') return withdrawals;
    return withdrawals.filter(w => w.currency === selectedCurrency);
  };

  const WithdrawalCard = ({ withdrawal }) => {
    const isPending = withdrawal.status === 'pending';
    const MethodIcon = getMethodIcon(withdrawal.method);
    
    return (
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <MethodIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold">{withdrawal.user_email || 'Customer'}</p>
                  <p className="text-sm text-muted-foreground">{getMethodLabel(withdrawal.method)}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(withdrawal.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground">Amount</p>
                  <p className="font-semibold text-xl text-blue-600">
                    {withdrawal.currency} {withdrawal.amount?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fee</p>
                  <p className="font-medium">{withdrawal.fee || 0} {withdrawal.currency}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  {getStatusBadge(withdrawal.status)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">ID</p>
                  <p className="text-xs font-mono">{withdrawal.withdrawal_id?.slice(0, 8)}</p>
                </div>
              </div>

              {/* Method-specific details */}
              {withdrawal.method === 'mobile_money' && withdrawal.mobile_money_details && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
                  <p className="text-sm font-semibold mb-2">📱 Mobile Money Details</p>
                  <div className="space-y-1 text-sm">
                    <p><strong>Provider:</strong> {withdrawal.mobile_money_provider_name || 'N/A'}</p>
                    {Object.entries(withdrawal.mobile_money_details).map(([key, value]) => (
                      <p key={key}><strong>{key}:</strong> {value}</p>
                    ))}
                  </div>
                </div>
              )}

              {withdrawal.method === 'bank' && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 mb-3">
                  <p className="text-sm font-semibold mb-2">🏦 Bank Details</p>
                  <div className="space-y-1 text-sm">
                    <p><strong>Bank:</strong> {withdrawal.bank_name || 'N/A'}</p>
                    <p><strong>Account:</strong> {withdrawal.bank_account_number || 'N/A'}</p>
                    <p><strong>Holder:</strong> {withdrawal.bank_account_holder || 'N/A'}</p>
                    {withdrawal.bank_branch_code && <p><strong>Branch:</strong> {withdrawal.bank_branch_code}</p>}
                    {withdrawal.bank_swift && <p><strong>SWIFT:</strong> {withdrawal.bank_swift}</p>}
                  </div>
                </div>
              )}

              {withdrawal.method === 'crypto' && (
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 mb-3">
                  <p className="text-sm font-semibold mb-2">₿ Crypto Details</p>
                  <div className="space-y-1 text-sm">
                    <p><strong>Network:</strong> {withdrawal.network || 'N/A'}</p>
                    <p className="break-all"><strong>Address:</strong> {withdrawal.crypto_address || 'N/A'}</p>
                  </div>
                </div>
              )}

              {withdrawal.note && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm"><strong>Note:</strong> {withdrawal.note}</p>
                </div>
              )}
            </div>

            {isPending && (
              <div className="flex flex-col gap-2 ml-4">
                <Button
                  size="sm"
                  onClick={() => handleProcess(withdrawal.withdrawal_id)}
                  disabled={processing === withdrawal.withdrawal_id}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Complete
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleReject(withdrawal.withdrawal_id)}
                  disabled={processing === withdrawal.withdrawal_id}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <AgentLayout title="Customer Withdrawals">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-lg">Loading withdrawals...</div>
        </div>
      </AgentLayout>
    );
  }

  const pending = filteredWithdrawals(pendingWithdrawals);
  const processed = filteredWithdrawals(processedWithdrawals);

  return (
    <AgentLayout title="Customer Withdrawals">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Customer Withdrawals
            </h1>
            <p className="text-muted-foreground">
              Process customer withdrawal requests (Mobile Money, Bank, Crypto, Cash)
            </p>
          </div>
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map(curr => (
                <SelectItem key={curr} value={curr}>
                  {curr === 'all' ? 'All Currencies' : curr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Clock className="h-8 w-8 mx-auto text-yellow-600 mb-2" />
                <p className="text-sm text-muted-foreground">Pending</p>
                <h3 className="text-2xl font-bold">{pending.length}</h3>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                <p className="text-sm text-muted-foreground">Completed</p>
                <h3 className="text-2xl font-bold">{processed.filter(w => w.status === 'completed').length}</h3>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Smartphone className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <p className="text-sm text-muted-foreground">Mobile Money</p>
                <h3 className="text-2xl font-bold">{pending.filter(w => w.method === 'mobile_money').length}</h3>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Landmark className="h-8 w-8 mx-auto text-green-600 mb-2" />
                <p className="text-sm text-muted-foreground">Bank</p>
                <h3 className="text-2xl font-bold">{pending.filter(w => w.method === 'bank').length}</h3>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Button
                variant={selectedTab === 'pending' ? 'default' : 'outline'}
                onClick={() => setSelectedTab('pending')}
              >
                Pending ({pending.length})
              </Button>
              <Button
                variant={selectedTab === 'processed' ? 'default' : 'outline'}
                onClick={() => setSelectedTab('processed')}
              >
                Processed ({processed.length})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {selectedTab === 'pending' && (
              <div>
                {pending.length === 0 ? (
                  <div className="text-center py-12">
                    <ArrowDownToLine className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No pending withdrawals</p>
                  </div>
                ) : (
                  pending.map(withdrawal => (
                    <WithdrawalCard key={withdrawal.withdrawal_id} withdrawal={withdrawal} />
                  ))
                )}
              </div>
            )}

            {selectedTab === 'processed' && (
              <div>
                {processed.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No processed withdrawals</p>
                  </div>
                ) : (
                  processed.map(withdrawal => (
                    <WithdrawalCard key={withdrawal.withdrawal_id} withdrawal={withdrawal} />
                  ))
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AgentLayout>
  );
};
